// Pages/JobRequisition/JobRequisitionNew.jsx

import { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

/** Lib Imports */
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import Toast from "@moltech/molui/toast";

/** Components */
import DynamicTabs from "../../components/dynamicTabs/DynamicTabs";
import JobRequisitionActions from "./JobRequisitionActions";
import Badges from "../../components/common/badges/Badges";
import NoRecordFound from "../../components/common/no-record-found/NoRecordFound";

/** Utilities */
import { encryptUrlData } from "../../utils/encryption/CryptoService";
import { formatDate } from "../../utils/common/dateFormat";
import { hasFunctionalPermission } from "../../utils/authentication/authorizeNavigation";
import { SwalService } from "../../utils/common/SwalService";
import { getAuthProps } from "../../utils/authentication/authLibrary";

/** ENUM */
import JobRequisitionStatus from "../../enums/JobRequisitionStatus";
import UserRoles from "../../enums/UserRoles.ENUM";
import { securityKeys } from "../../enums/SecurityKeys.ENUM";
import { MasterDataCacheURL } from "../../enums/CacheURL";

/** RTK Query */
import {
  useDeleteDataMutation,
  useLazyGetGridDataQuery,
  usePostDataMutation,
} from "../../app/services/commonApi";
import { useMasterData } from "../../app/hooks/useMasterData";

/** CSS */
import "./Requisition.scss";

const getStatusBadgeProps = (status, label) => {
  switch (status) {
    case JobRequisitionStatus.DRAFT:
      return { label: label || "Draft", type: "warning" };

    case JobRequisitionStatus.PENDING_APPROVAL:
      return { label: label || "Pending Approval", type: "info" };

    case JobRequisitionStatus.APPROVED:
      return { label: label || "Approved", type: "success" };

    case JobRequisitionStatus.REJECTED:
      return { label: label || "Rejected", type: "danger" };

    case JobRequisitionStatus.CANCELLED:
      return { label: label || "Cancelled", type: "gray" };

    case JobRequisitionStatus.ON_HOLD:
      return { label: label || "On Hold", type: "gray" };

    default:
      return { label: label || "Unknown", type: "info" };
  }
};

const renderStatus = (rowData) => {
  const status = rowData.status || rowData.requisitionStatusId;
  const { label, type } = getStatusBadgeProps(
    status,
    rowData.requisitionStatusName,
  );
  return <Badges label={label} type={type} showDot={true} />;
}

const renderSalaryRange = (rowData) => {
  const low = rowData.expectedSalaryLow;
  const high = rowData.expectedSalaryHigh;

  if (!low && !high) return <span>-</span>;

  const lowStr = low ? `₹${low.toLocaleString('en-IN')}` : "";
  const highStr = high ? `₹${high.toLocaleString('en-IN')}` : "";

  if (lowStr && highStr) return <span>{`${lowStr} – ${highStr}`}</span>;
  if (lowStr) return <span>{lowStr}</span>;
  if (highStr) return <span>{highStr}</span>;

  return <span>-</span>;
};

const renderSkills = (rowData, skillMap) => {
  const skills = rowData.skills;

  if (!skills || !Array.isArray(skills) || skills.length === 0) {
    return <span style={{ color: '#999' }}>No skills</span>;
  }

  // Map skill IDs to names using the skillMap from component state
  const skillNames = skills.map(skillId => {
    const skillName = skillMap[skillId]
    return skillName || null;
  }).filter(Boolean);

  if (skillNames.length === 0) {
    return <span style={{ color: '#999' }}>No skills</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {skillNames.slice(0, 3).map((skillName, index) => (
        <span
          key={index + 1}
          style={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {skillName}
        </span>
      ))}
      {skillNames.length > 3 && (
        <span style={{ color: '#666', fontSize: '12px' }}>
          +{skillNames.length - 3} more
        </span>
      )}
    </div>
  );
};

const renderBudgetAvailability = (rowData) => {
  const budget = rowData.budgetAvailability;

  if (!budget) return <span>-</span>;

  const trimmedBudget = budget.toString().trim();

  if (trimmedBudget.toLowerCase() === 'yes') {
    return <Badges label="Yes" type="success" showDot={false} />;
  } else if (trimmedBudget.toLowerCase() === 'no') {
    return <Badges label="No" type="danger" showDot={false} />;
  }

  return <span>-</span>;
};

const renderUrgencyLevel = (rowData) => {
  const urgency = rowData.urgencyLevel;

  if (!urgency) return <span>-</span>;

  const trimmedUrgency = urgency.toString().trim().toLowerCase();

  switch (trimmedUrgency) {
    case 'high':
      return <Badges label="High" type="danger" showDot={false} />;
    case 'medium':
      return <Badges label="Medium" type="warning" showDot={false} />;
    case 'low':
      return <Badges label="Low" type="success" showDot={false} />;
    default:
      return <span>-</span>;
  }
};

const JobRequisitionNew = () => {
  const gridRef = useRef();
  const [deleteApplicant] = useDeleteDataMutation();
  const [updateStatus] = usePostDataMutation();
  const navigate = useNavigate();
  const { confirm } = SwalService;

  const authProps = getAuthProps();

  const roleId = Number(authProps?.roles?.roleId) || null;
  const [skillMap, setSkillMap] = useState({});
  const [gridData, setGridData] = useState(null);

  const { data: triggerGetStatusName } = useMasterData(MasterDataCacheURL.GetAllStatus);
  const { data: skillData } = useMasterData(MasterDataCacheURL.GetAllSkills);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);

  const approvalLevelMap = {
    1: "Interviewer",
    2: "HR",
    3: "Admin",
  };

  const statusOptions = useMemo(() => {
    if (!Array.isArray(triggerGetStatusName)) return [];
    return triggerGetStatusName.map((item) => ({
      label: item?.requisitionStatusName,
      value: item.requisitionStatusId,
    }));
  }, [triggerGetStatusName]);

  const getFilteredTabs = () => {
    switch (roleId) {
      case UserRoles.ADMIN:
        return [
          { header: "My Approvals", value: JobRequisitionStatus.APPROVED },
        ];
      case UserRoles.HR: {
        const hrStatusIds = new Set([
          JobRequisitionStatus.PENDING_APPROVAL,
          JobRequisitionStatus.APPROVED,
          JobRequisitionStatus.REJECTED,
          JobRequisitionStatus.ON_HOLD,
        ]);
        const tabs = statusOptions
          .filter((s) => hrStatusIds.has(s.value))
          .map((s) => ({ header: s.label, value: s.value }));
        return [{ header: "All", value: 0 }, ...tabs];
      }
      case UserRoles.INTERVIEWER: {
        const intStatusIds = new Set([
          JobRequisitionStatus.DRAFT,
          JobRequisitionStatus.PENDING_APPROVAL,
          JobRequisitionStatus.APPROVED,
          JobRequisitionStatus.REJECTED,
          JobRequisitionStatus.CANCELLED,
          JobRequisitionStatus.ON_HOLD,
        ]);
        return statusOptions
          .filter((s) => intStatusIds.has(s.value))
          .map((s) => ({
            header: s.label,
            value: s.value,
          }));
      }
      default:
        return [];
    }
  };

  const getFilteredDropdown = () => {
    switch (roleId) {
      case UserRoles.ADMIN:
        return statusOptions.map((s) => ({
          label: s.label,
          value: s.value,
        }));
      case UserRoles.HR:
      case UserRoles.INTERVIEWER:
        return tabs.map((t) => ({ label: t.header, value: t.value }));
      default:
        return [];
    }
  };

  const tabs = getFilteredTabs();
  const dropdownOptions = getFilteredDropdown();

  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(0);

  useEffect(() => {
    if (UserRoles.INTERVIEWER || UserRoles.ADMIN || statusOptions.length > 0) {
      if (tabs.length > 0) {
        setActiveIndex(0);
        setSelectedStatus(tabs[0].value);
      }

      setTimeout(() => {
        gridRef.current?.reload();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions, roleId]);

  // Fetch skill master data
  useEffect(() => {
    if (skillData && Array.isArray(skillData)) {      // Create lookup map for performance
      const map = {};
      skillData.forEach(skill => {
        map[skill.skillId] = skill.skillName;
      });
      setSkillMap(map);
    } else {
      setSkillMap({});
    }
  }, [skillData]);

  const hasAddPermission = hasFunctionalPermission(
    securityKeys.ADD_JOB_REQUISITION,
  );
  const hasEditPermission = hasFunctionalPermission(
    securityKeys.UPDATE_JOB_REQUISITION,
  );
  const hasDeletePermission = hasFunctionalPermission(
    securityKeys.DELETE_JOB_REQUISITION,
  );

  const handleAddRequisition = (requisitionData) => {
    if (requisitionData) {
      let jobRequisitionId = requisitionData.jobRequisitionId;
      navigate(`/update-job-requisition/${encryptUrlData(jobRequisitionId)}`);
    } else {
      navigate("/add-requisition");
    }
  };

  const onDelete = (rowData) => {

    confirm({
      text: "Would you like to delete this Job Requisition?"
    })
      .then((confirmed) => {
        if (confirmed) {
          deleteApplicant({
            url: "/JobRequisition/DeleteJobRequisition",
            data: { jobRequisitionId: rowData.jobRequisitionId },
          }).then((res) => {
            if (onCallback) {
              onCallback({
                message:
                  res.data?.message || "Job Requisition deleted successfully!",
              });
            }
          });
        }
      });
  };

  const onCallback = ({ message }) => {
    gridRef.current.reload();
    Toast.success(message);
  };

  const handleStatusUpdate = (row, newStatus, actionName, reason = "") => {
    if (
      roleId === UserRoles.ADMIN &&
      newStatus === JobRequisitionStatus.REJECTED
    ) {
      setSelectedRow(row);
      setPendingStatus(newStatus);
      setShowRejectModal(true);
      return;
    }

    let approvalLevel = row?.approvalLevel;

    if (actionName === "Submit" || actionName === "Resubmit") {
      newStatus = JobRequisitionStatus.PENDING_APPROVAL;
      approvalLevel = 2; // Move to HR
    } else if (actionName === "Approve") {
      if (roleId === UserRoles.HR) {
        newStatus = JobRequisitionStatus.PENDING_APPROVAL;
        approvalLevel = 3; // Move to Admin
      } else if (roleId === UserRoles.ADMIN) {
        newStatus = JobRequisitionStatus.APPROVED;
        approvalLevel = 2;
        // Level remains 3 or Final
      }
    } else if (actionName === "Reject") {
      newStatus = JobRequisitionStatus.REJECTED;
      approvalLevel = 1; // Reset to Interviewer
    } else if (actionName === "Hold") {
      newStatus = JobRequisitionStatus.ON_HOLD;
    } else if (actionName === "Resume") {
      newStatus = JobRequisitionStatus.PENDING_APPROVAL;
    } else if (actionName === "Cancel") {
      newStatus = JobRequisitionStatus.CANCELLED;
    }

    const callApi = () => {
      updateStatus({
        url: "/JobRequisition/UpdateJobRequisitionStatus",
        data: {
          ...row,
          jobRequisitionId: row.jobRequisitionId,
          status: newStatus,
          ApprovalLevel: approvalLevel,
          rejectReason: reason,
        },
      }).then((res) => {
        if (res?.error) {
          Toast.error(res.error?.data || "Action failed!");
        } else {
          onCallback({
            message:
              res.data?.message || "Job Requisition updated successfully!",
          });
        }
      });
    };

    if (
      actionName === "Submit" ||
      actionName === "Resubmit" ||
      actionName === "Approve" ||
      actionName === "Hold" ||
      actionName === "Resume"
    ) {
      callApi();
      return;
    }

    const actionText = actionName
      ? actionName.toLowerCase()
      : "perform this action";
    confirm(
      "Confirm?",
      `Are you sure you want to ${actionText} this Job Requisition?`,
    ).then((confirmed) => {
      if (confirmed) {
        callApi();
      }
    });
  };

  const handleRejectSave = () => {
    if (!rejectReason.trim()) {
      Toast.error("Please enter reject reason");
      return;
    }

    const approvalLevel = 1;
    updateStatus({
      url: "/JobRequisition/UpdateJobRequisitionStatus",
      data: {
        ...selectedRow,
        jobRequisitionId: selectedRow.jobRequisitionId,
        status: pendingStatus,
        ApprovalLevel: approvalLevel,
        RejectedReason: rejectReason,
      },
    }).then((res) => {
      if (res?.error) {
        Toast.error(res.error?.data || "Action failed!");
      } else {
        onCallback({
          message: res.data?.message || "Job Requisition updated successfully!",
        });
      }
    });

    setShowRejectModal(false);
    setRejectReason("");
    setSelectedRow(null);
    setPendingStatus(null);
  };

  const beforeGridLoad = (reqPrm) => {
    const req = {
      pageIndex: Math.floor(reqPrm.first / reqPrm.rows) + 1,
      pageSize: reqPrm.rows,
      SortField: reqPrm.sortField,
      SortOrder: reqPrm.sortOrder === 1 ? "ASC" : "DESC",
      statusId:
        roleId === UserRoles.ADMIN
          ? selectedStatus
          : tabs[activeIndex]?.value || 0,
    };
    return req;
  };

  const afterGridLoad = (res) => {
    const paginatedResult = res?.data;
    setGridData(paginatedResult ?? null);
    return {
      data: paginatedResult?.data ?? [],
      count: paginatedResult?.count ?? 0,
    };
  };

  const handleActionButton = (row) => (
    <div className="requisition-action-container">
      <JobRequisitionActions
        row={row}
        onEdit={handleAddRequisition}
        onDelete={onDelete}
        onAction={handleStatusUpdate}
        roleId={roleId}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
      />
    </div>
  );

  const onTabChange = (index) => {
    setActiveIndex(index);
    const selectedTab = tabs[index];
    if (roleId !== UserRoles.ADMIN) {
      setSelectedStatus(selectedTab.value);
    }
    setGridData([]);
    gridRef.current.reload();
  };

  const onStatusChange = (e) => {
    setSelectedStatus(e.value);
    setGridData([]);

    if (roleId === UserRoles.ADMIN) {
      gridRef.current.reload();
    }
  };

  const tabsData = tabs.map((tab) => ({
    header: tab.header,
    content: <></>,
  }));



  return (
    <>
      <Card className="custom-card height-full-grid-with-searchbar">
        <div className="page-header">
          <div className="left-title-part">
            <h2 className="card-title">Job Requisition</h2>
          </div>
          <div className="right-btn-part d-flex align-items-center gap-2">
            <div className="status-dropdown">
              <Dropdown
                value={selectedStatus}
                options={dropdownOptions}
                onChange={onStatusChange}
                placeholder="Select Status"
                className="w-full md:w-14rem"
                disabled={roleId !== UserRoles.ADMIN}
              />
            </div>
            <Button
              className="btn secondary-btn"
              icon="pi pi-plus"
              tooltip="Add Job Requisition"
              data-pr-position="left"
              disabled={!hasAddPermission?.isEditable}
              onClick={() => handleAddRequisition()}
            />
          </div>
        </div>

        <div className="mb-3">
          <DynamicTabs
            tabs={tabsData}
            activeIndex={activeIndex}
            onTabChange={onTabChange}
          />
        </div>

        {gridData?.data?.length === 0 ? (
          <NoRecordFound message="No Job Requisition Found" />
        ) : (
          <MolGrid
            ref={gridRef}
            url="/JobRequisition/GetAllRequisitionByStatusId"
            client={useLazyGetGridDataQuery}
            afterLoad={afterGridLoad}
            beforeLoad={beforeGridLoad}
            paginator
            rows={25}
            rowsPerPageOptions={[10, 25, 50]}
            scrollable
            scrollHeight="flex"
            filterDisplay="none"
            paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate="{last} of {totalRecords}"
          >
            <Column field="requisitionNumber" header="Requisition No." hideColumnMenu={true} />
            <Column field="designation" header="Designation" hideColumnMenu={true} />
            <Column field="noOfPositions" header="No. of Position" hideColumnMenu={true} />
            <Column field="departmentName" header="Department" hideColumnMenu={true} />
            <Column
              header="Status"
              headerClassName="center-grid-data"
              bodyClassName="center-grid-data"
              body={renderStatus}
              sortable={false}
              hideColumnMenu={true}
            />
            <Column
              header="Approval Level"
              body={(rowData) => approvalLevelMap[rowData.approvalLevel] || ""}
              sortable={false}
              hideColumnMenu={true}
            />
            <Column field="requestByName" header="Requested By" hideColumnMenu={true} />
            <Column
              field="createdAt"
              header="Requested Date"
              body={(rowData) => {
                return formatDate(rowData.createdAt);
              }}
              hideColumnMenu={true}
            />
            <Column
              header="Salary Range"
              body={renderSalaryRange}
              sortable={false}
              style={{ minWidth: "120px", maxWidth: "150px" }}
              hideColumnMenu={true}
            />
            <Column
              header="Skills"
              body={(rowData) => renderSkills(rowData, skillMap)}
              sortable={false}
              style={{ minWidth: "200px", maxWidth: "250px" }}
              hideColumnMenu={true}
            />
            <Column
              header="Budget"
              body={renderBudgetAvailability}
              headerClassName="center-grid-data"
              bodyClassName="center-grid-data"
              sortable={false}
              style={{ width: "80px" }}
              hideColumnMenu={true}
            />
            <Column
              header="Urgency"
              body={renderUrgencyLevel}
              headerClassName="center-grid-data"
              bodyClassName="center-grid-data"
              sortable={false}
              style={{ width: "90px" }}
              hideColumnMenu={true}
            />
            <Column
              style={{ width: "5rem" }}
              hideColumnMenu
              header="Actions"
              body={handleActionButton}
              sortable={false}
            />
          </MolGrid>
        )}
      </Card>
      <Dialog
        header="Reject Job Requisition"
        visible={showRejectModal}
        style={{ width: "400px" }}
        onHide={() => setShowRejectModal(false)}
      >
        <div className="flex flex-column gap-3">
          <InputTextarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={5}
            placeholder="Enter reject reason"
          />

          <div className="flex justify-content-end mt-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowRejectModal(false)}
            />
            <Button label="Reject Requisition" onClick={handleRejectSave} />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default JobRequisitionNew;
